import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import AddCompanyModal from '../components/AddCompanyModal';
import EditCompanyModal from '../components/EditCompanyModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Outhouse Companies</h1>
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search by company name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-80"
            />
          </div>
          <Button 
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} className="mr-2" />
            Add New Company
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Company # ▼</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Company Name ▼</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">First Name ▼</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Last Name ▼</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Email ▼</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone ▼</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Role ▼</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-8 text-gray-500">No companies found</td>
              </tr>
            ) : (
              filteredCompanies.map((company, idx) => (
                <tr key={company.email} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-900">{idx + 1}</td>
                  <td className="px-4 py-3 text-gray-900 font-medium">{company.company || company.name}</td>
                  <td className="px-4 py-3 text-gray-600">{company.first_name}</td>
                  <td className="px-4 py-3 text-gray-600">{company.last_name}</td>
                  <td className="px-4 py-3 text-blue-600">{company.email}</td>
                  <td className="px-4 py-3 text-gray-600">{company.phone}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      company.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {company.role === 'admin' ? 'Admin' : 'Syndicator'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedCompany(company);
                        setShowEditModal(true);
                      }}
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AddCompanyModal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)}
        onSuccess={loadCompanies}
      />

      <EditCompanyModal 
        isOpen={showEditModal} 
        onClose={() => {
          setShowEditModal(false);
          setSelectedCompany(null);
        }}
        onSuccess={loadCompanies}
        company={selectedCompany}
      />
    </div>
  );
};

export default Companies;